// apps/admin/src/pages/auth/VerifyMFAPage.tsx
//
// Route: /mfa/verify — native GCIP sign-in challenge for the founder admin
// portal. Re-pointed off the bespoke verifyLoginMFA callable and the
// sessionStorage `admin_mfa_verified` gate onto the modular Web SDK: the login
// surface captures the live MultiFactorResolver (which cannot cross router
// history) and this page reads it. A direct visit or refresh finds no pending
// resolver and returns to /login, where signing in re-issues a fresh challenge.
//
// The read is a non-consuming peek held in state (finding M4, 2026-08-17). The
// previous single-use `take` behind a ref guard did NOT survive
// React.StrictMode's double render on mount: the second pass re-runs the hooks
// from scratch, so it got a fresh ref, took again, got null, and React kept
// THAT pass, bouncing the founder to /login after a correct password on every
// dev run. A peek is idempotent under any number of render passes; the holder
// is cleared explicitly once the challenge completes.

import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import MfaChallengeCard from '../../components/auth/MfaChallengeCard';
import { clearPendingMfaResolver, peekPendingMfaResolver } from '../../lib/nativeMfa';

const VerifyMFAPage: React.FC = () => {
  const navigate = useNavigate();

  // Held in state so the page keeps the resolver for its whole life even after
  // the holder is cleared on success; the lazy initialiser is safe to run twice.
  const [resolver] = useState(() => peekPendingMfaResolver());

  if (!resolver) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-900 bg-gradient-to-br from-indigo-900/50 via-gray-900 to-fuchsia-900/50 text-white">
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 -left-1/4 w-96 h-96 bg-indigo-600/30 rounded-full filter blur-3xl opacity-50 animate-pulse"></div>
        <div className="absolute bottom-0 -right-1/4 w-96 h-96 bg-fuchsia-400/30 rounded-full filter blur-3xl opacity-50 animation-delay-4000 animate-pulse"></div>
      </div>
      <MfaChallengeCard
        resolver={resolver}
        onSuccess={() => {
          // Fully signed in and the resolver is spent: drop it so a later visit
          // to /mfa/verify cannot render a challenge that can never complete.
          // AdminContext recomputes isFounder + mfaEnrolled and AdminRouteGuard
          // admits the founder to the portal.
          //
          // The clear is ownership-checked (Codex CX-M4-1): this page may be a
          // stale one whose challenge only just finished, and the holder may
          // already carry a NEWER resolver from a second sign-in. Navigation is
          // unconditional either way, because this founder IS signed in.
          clearPendingMfaResolver(resolver);
          navigate('/', { replace: true });
        }}
      />
    </div>
  );
};

export default VerifyMFAPage;
